import React, { useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { FaCamera, FaPrint, FaCreditCard, FaShieldAlt, FaRocket, FaDesktop, FaDownload } from 'react-icons/fa'
import type { IconType } from 'react-icons'

/**
 * Settings navigation — ONE component, two orientations.
 *
 * The modal used to fork into three separate JSX trees (a reduced kiosk card, a
 * compact mobile card, a full desktop card), and that fork is what left Android
 * booths unable to reach printer settings at all for months: the branch Android
 * rendered simply didn't contain them.
 *
 * So orientation is a prop here, not a different screen. `rail` is the desktop /
 * landscape vertical list; `strip` is the horizontally scrolling segmented control
 * for portrait kiosks and phones. Same groups, same order, same status dots, same
 * code path — only the axis changes.
 */

export type SettingsGroupId =
  | 'camera'
  | 'printing'
  | 'payments'
  | 'lockdown'
  | 'startup'
  | 'screen'
  | 'files'

/**
 * What the dot next to a group means. `warn` is for "an operator standing here
 * would want to know" — no printer selected while printing is on, camera not
 * connected — NOT for errors the operator can't act on.
 */
export type SettingsGroupStatus = 'ok' | 'warn' | 'idle'

interface SettingsGroupDef {
  id: SettingsGroupId
  /** i18n key under `settings.groups`, with an English fallback at the call site. */
  labelKey: string
  fallback: string
  Icon: IconType
  /**
   * A non-clickable label the rail draws above the first entry of a run that
   * shares it — "Booth" over Lockdown / Startup / Screen. The strip has no room
   * for it and shows the entries alone.
   */
  header?: { labelKey: string; fallback: string }
}

const BOOTH_HEADER = { labelKey: 'settings.groups.booth', fallback: 'Booth' }

/**
 * Order is deliberate: the two things an operator opens settings to fix (camera,
 * printer) come first, then the booth's kiosk behaviour as three pages grouped
 * by PURPOSE under one "Booth" header — keep guests in, come back after a power
 * cut, what the screen does — then the rarely-touched file settings. Payments
 * slots in after `printing`.
 */
export const SETTINGS_GROUPS: readonly SettingsGroupDef[] = [
  { id: 'camera', labelKey: 'settings.groups.camera', fallback: 'Camera', Icon: FaCamera },
  { id: 'printing', labelKey: 'settings.groups.printing', fallback: 'Printing', Icon: FaPrint },
  { id: 'payments', labelKey: 'settings.groups.payments', fallback: 'Payments', Icon: FaCreditCard },
  {
    id: 'lockdown',
    labelKey: 'settings.boothGroups.lockdown',
    fallback: 'Lockdown',
    Icon: FaShieldAlt,
    header: BOOTH_HEADER
  },
  {
    id: 'startup',
    labelKey: 'settings.boothGroups.startup',
    fallback: 'Startup',
    Icon: FaRocket,
    header: BOOTH_HEADER
  },
  {
    id: 'screen',
    labelKey: 'settings.boothGroups.screen',
    fallback: 'Screen',
    Icon: FaDesktop,
    header: BOOTH_HEADER
  },
  { id: 'files', labelKey: 'settings.groups.files', fallback: 'Files', Icon: FaDownload }
] as const

const DOT_CLASS: Record<SettingsGroupStatus, string> = {
  ok: 'bg-green-600',
  warn: 'bg-amber-600',
  idle: 'bg-gray-300'
}

interface SettingsNavProps {
  variant: 'rail' | 'strip'
  active: SettingsGroupId
  onSelect: (id: SettingsGroupId) => void
  /** Per-group dot. Groups left out render as `idle`. */
  status?: Partial<Record<SettingsGroupId, SettingsGroupStatus>>
  /** Project brand colour, so the active item matches the rest of the modal. */
  primaryColor: string
  /** Tighter type/padding for short portrait viewports. */
  compact?: boolean
}

export const SettingsNav: React.FC<SettingsNavProps> = ({
  variant,
  active,
  onSelect,
  status,
  primaryColor,
  compact = false
}) => {
  const { t } = useTranslation()
  const listRef = useRef<HTMLDivElement>(null)


  // Roving arrow-key movement. Kiosks are touch-first, but the modal is also
  // driven from a keyboard on the operator's laptop during setup.
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>): void => {
      const forward = variant === 'rail' ? 'ArrowDown' : 'ArrowRight'
      const back = variant === 'rail' ? 'ArrowUp' : 'ArrowLeft'
      if (event.key !== forward && event.key !== back) return
      event.preventDefault()
      const index = SETTINGS_GROUPS.findIndex((group) => group.id === active)
      if (index === -1) return
      const delta = event.key === forward ? 1 : -1
      const next = SETTINGS_GROUPS[(index + delta + SETTINGS_GROUPS.length) % SETTINGS_GROUPS.length]
      onSelect(next.id)
      // Move focus with the selection so the next arrow press continues from here.
      listRef.current
        ?.querySelector<HTMLButtonElement>(`[data-group="${next.id}"]`)
        ?.focus()
    },
    [active, onSelect, variant]
  )

  const isRail = variant === 'rail'

  useEffect(() => {
    listRef.current
      ?.querySelector<HTMLButtonElement>(`[data-group="${active}"]`)
      ?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }, [active])

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-orientation={isRail ? 'vertical' : 'horizontal'}
      aria-label={t('settings.groups.navLabel', 'Settings sections')}
      onKeyDown={handleKeyDown}
      className={
        isRail
          ? 'flex w-40 shrink-0 flex-col gap-0.5 overflow-y-auto border-r border-gray-200 bg-gray-50 p-2'
          : 'custom-scrollbar flex shrink-0 gap-1.5 overflow-x-auto overscroll-x-contain border-b border-gray-200 bg-gray-50 px-3 py-2'
      }
    >
      {SETTINGS_GROUPS.map(({ id, labelKey, fallback, Icon, header }, index) => {
        const isActive = id === active
        const dot = DOT_CLASS[status?.[id] ?? 'idle']
        const previous = SETTINGS_GROUPS[index - 1]
        const opensHeader = isRail && header && previous?.header?.labelKey !== header.labelKey
        const closesHeader = isRail && !header && Boolean(previous?.header)
        return (
          <React.Fragment key={id}>
          {opensHeader && (
            <div
              className={`px-2.5 pb-1 pt-3 font-semibold uppercase tracking-wide text-gray-500 ${
                compact ? 'text-[10px]' : 'text-[11px]'
              }`}
              aria-hidden="true"
            >
              {t(header.labelKey, header.fallback)}
            </div>
          )}
          <button
            type="button"
            role="tab"
            data-group={id}
            aria-selected={isActive}
            // Only the selected tab stays in the tab order; arrows move within.
            tabIndex={isActive ? 0 : -1}
            onClick={() => onSelect(id)}
            style={
              isActive
                ? { backgroundColor: `${primaryColor}1A`, color: primaryColor }
                : undefined
            }
            className={
              isRail
                ? `flex min-h-[2.75rem] w-full items-center gap-2 rounded-md px-2.5 text-left transition-colors ${
                    compact ? 'py-1.5 text-xs' : 'py-2 text-sm'
                  } ${closesHeader ? 'mt-3' : ''} ${
                    isActive
                      ? 'font-semibold'
                      : 'text-gray-700 hover:bg-gray-100 active:bg-gray-200'
                  }`
                : `flex min-h-[2.5rem] shrink-0 items-center gap-2 whitespace-nowrap rounded-md border px-3 transition-colors ${
                    compact ? 'py-1.5 text-xs' : 'py-2 text-sm'
                  } ${
                    isActive
                      ? 'border-transparent font-semibold'
                      : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-100 active:bg-gray-200'
                  }`
            }
          >
            <Icon
              className={compact ? 'h-3.5 w-3.5 shrink-0' : 'h-4 w-4 shrink-0'}
              aria-hidden="true"
            />
            <span className="min-w-0 flex-1 truncate">{t(labelKey, fallback)}</span>
            <span
              className={`h-1.5 w-1.5 shrink-0 rounded-full ${dot}`}
              aria-hidden="true"
            />
          </button>
          </React.Fragment>
        )
      })}
    </div>
  )
}

export default SettingsNav
